import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Tag,
  Divider,
  Spin,
  App,
  Popconfirm,
  Collapse,
  Select,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  DeleteOutlined,
  CarOutlined,
  FileTextOutlined,
  LineChartOutlined,
  SwapOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSalesOrder, useUpdateSalesOrderStatus, useApproveSalesOrder } from '../../hooks/useSalesOrder';
import { useOpportunity } from '../../hooks/useOpportunities';
import { useCreateRetrospectiveDelivery } from '../../hooks/useDelivery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import deliveryService from '../../services/delivery.service';
import { SalesOrderItem } from '../../services/salesOrder.service';
import salesOrderService from '../../services/salesOrder.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { CreateAdvanceInvoiceModal } from '../../components/sales-invoice';
import DocumentList from '../../components/documents/DocumentList';
import InheritedAttachmentsCard from '../../components/documents/InheritedAttachmentsCard';
import DocumentUpload from '../../components/documents/DocumentUpload';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { quotationConsolidationService } from '@/services/quotationConsolidation.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_SALES_ORDER } from '@/constants/approval-entity-types';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import { ExplainIntelligenceButton } from '@/components/ai-agent/ExplainIntelligenceButton';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { RelatedMocsPanel } from '@/components/moc/RelatedMocsPanel';
import { useSystemSettings } from '@/hooks/useSettings';
import PrintableSalesOrder from '@/components/sales/PrintableSalesOrder';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import SalesOrderAddressModal from '@/components/sales/SalesOrderAddressModal';

const { Title, Text } = Typography;

const SalesOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { data: settings } = useSystemSettings();
  const [soSubmitLoading, setSoSubmitLoading] = useState(false);
  const { data: order, isLoading } = useSalesOrder(id);
  const { data: departments = [] } = useDepartmentsForSelection();
  const linkedOppId = order?.opportunity_id?.trim() ? order.opportunity_id.trim() : undefined;
  const { data: linkedOpportunity } = useOpportunity(linkedOppId);
  const updateStatusMutation = useUpdateSalesOrderStatus();
  const createRetrospectiveMutation = useCreateRetrospectiveDelivery();
  const approveOrderMutation = useApproveSalesOrder();
  const { data: deliveriesForSo = [] } = useQuery({
    queryKey: ['deliveries', 'by-so', order?.id],
    queryFn: () => deliveryService.list({ sales_order_id: order!.id }),
    enabled: !!order?.id && order.order_status !== 'Draft' && order.order_status !== 'Cancelled',
    staleTime: 30000,
  });
  const { data: consolidationLinks = [] } = useQuery({
    queryKey: ['so-quotation-links', id],
    queryFn: () => quotationConsolidationService.getLinksBySalesOrder(id!),
    enabled: !!id,
    staleTime: 60000,
  });
  const recordSalesReviewMutation = useMutation({
    mutationFn: (salesOrderId: string) => salesOrderService.recordSalesReview(salesOrderId),
    onSuccess: (_data, salesOrderId) => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders', salesOrderId] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({
        queryKey: ['approval-summary', ENTITY_TYPE_SALES_ORDER, salesOrderId],
      });
      message.success('Sales review recorded. Approvers have been notified.');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Failed to record sales review');
    },
  });

  const closeTimesheetOrderMutation = useMutation({
    mutationFn: (salesOrderId: string) => salesOrderService.closeTimesheetFulfillment(salesOrderId),
    onSuccess: (_data, salesOrderId) => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders', salesOrderId] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      message.success('Customer order closed. No further timesheets or invoices can be created.');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Failed to close customer order');
    },
  });

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const revenueServiceTypeLabel = useMemo(() => {
    if (!order?.revenue_service_type_id) return '—';
    const r = revenueServiceTypes.find((x) => x.id === order.revenue_service_type_id);
    return r ? `${r.service_code} — ${r.service_name}` : '—';
  }, [order?.revenue_service_type_id, revenueServiceTypes]);
  const [, setSelectedStatus] = useState<string | null>(null);
  const [showAdvanceInvoiceModal, setShowAdvanceInvoiceModal] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [addressModal, setAddressModal] = useState<{ open: boolean; approveAfter: boolean }>({
    open: false,
    approveAfter: false,
  });

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const breadcrumbSegments = useMemo(
    () =>
      order
        ? [
            { title: 'Growth & Revenue', path: '/growth/dashboard' },
            { title: 'Customer orders', path: '/growth/orders' },
            { title: order.sales_order_number || 'Order' },
          ]
        : null,
    [order?.id, order?.sales_order_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const delivered = order?.order_status === 'Delivered';
  const cancelled = order?.order_status === 'Cancelled';
  const draft = order?.order_status === 'Draft';
  const workflowCurrent =
    !order ? 0 : draft ? 0 : cancelled ? 0 : delivered ? 2 : 1;
  const workflowStages = useMemo(() => {
    if (!order) return [];
    const d = order.order_status === 'Delivered';
    const c = order.order_status === 'Cancelled';
    const dr = order.order_status === 'Draft';
    return [
      {
        title: 'Order',
        description: order.order_status,
        status: (c ? 'error' : dr ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Delivery',
        description: d ? 'Delivered' : 'Pending',
        status: (c ? 'wait' : d ? 'finish' : dr ? 'wait' : 'process') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Invoice',
        description: d ? 'From delivered notes (Tax Invoices)' : 'After delivery',
        status: (d ? 'process' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Collection',
        description: 'Receipt / settlement',
        status: 'wait' as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [order]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <Card>
        <Text type="secondary">Customer Order not found</Text>
      </Card>
    );
  }

  // Status color mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft':
        return 'default';
      case 'Confirmed':
        return 'blue';
      case 'In Production':
        return 'orange';
      case 'Ready for Delivery':
        return 'cyan';
      case 'Delivered':
        return 'green';
      case 'Completed':
        return 'purple';
      case 'Cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  const handleBack = () => {
    navigate('/growth/orders');
  };

  const handleEdit = () => {
    navigate(`/growth/orders/edit/${id}`);
  };

  const handlePrint = () => {
    if (!order) return;
    const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
    const getSetting = (key: string) => companySettings.find((s: any) => s.setting_key === key)?.setting_value ?? '';
    const companyInfo = {
      name: getSetting('company_name') || 'Company Name',
      address: getSetting('company_address') || '',
      phone: getSetting('company_phone') || '',
      email: getSetting('company_email') || '',
      taxNumber: getSetting('tax_registration_number') ? `TRN: ${getSetting('tax_registration_number')}` : '',
    };

    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(
      <PrintableSalesOrder
        order={order}
        revenueServiceTypeLabel={revenueServiceTypeLabel === 'â€”' ? undefined : revenueServiceTypeLabel}
        companyInfo={companyInfo}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateStatusMutation.mutateAsync({ id: order.id, status: newStatus });
      setSelectedStatus(null);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  /** NM_WP_66: approvers are gated until sales records review */
  const salesReviewPending = order.sales_review_status === 'pending_review';
  const missingShipTo = !String(order.delivery_address ?? '').trim();
  const missingBillTo = !String(order.customer_address ?? '').trim();
  const awaitingApproval = order.approval_status !== 'Approved' && order.order_status !== 'Cancelled';
  const missingCustomerPo = !String(order.customer_po_number ?? '').trim();
  const missingCustomerPoDate = !String(order.customer_po_date ?? '').trim();
  const missingCustomerPoFields = missingCustomerPo || missingCustomerPoDate;

  // Line items columns
  const columns: ColumnsType<SalesOrderItem> = [
    {
      title: '#',
      dataIndex: 'line_number',
      key: 'line_number',
      width: 60,
      align: 'center'
    },
    {
      title: 'Item Code',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 120
    },
    {
      title: 'Description',
      dataIndex: 'item_description',
      key: 'item_description',
      width: 250
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      width: 80,
      render: (v: string) => v || '—',
    },
    {
      title: 'Ordered Qty',
      dataIndex: 'ordered_quantity',
      key: 'ordered_quantity',
      width: 100,
      align: 'right',
      render: (value: number, record: SalesOrderItem) => 
        `${toNumber(value).toFixed(2)} ${record.uom || ''}`
    },
    {
      title: 'Delivered Qty',
      dataIndex: 'delivered_quantity',
      key: 'delivered_quantity',
      width: 100,
      align: 'right',
      render: (value: number, record: SalesOrderItem) => 
        `${toNumber(value).toFixed(2)} ${record.uom || ''}`
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (value: number) => formatCurrency(toNumber(value), order.currency)
    },
    {
      title: 'Discount %',
      dataIndex: 'discount_percentage',
      key: 'discount_percentage',
      width: 100,
      align: 'right',
      render: (value: number) => `${toNumber(value).toFixed(2)}%`
    },
    {
      title: 'Line Total',
      dataIndex: 'line_total',
      key: 'line_total',
      width: 120,
      align: 'right',
      render: (value: number) => (
        <Text strong>{formatCurrency(toNumber(value), order.currency)}</Text>
      )
    }
  ];

  /* Fragment (not nested Space) so EnterprisePageHeader can wrap each button */
  const primaryActions = (
    <>
      <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
        Back
      </Button>
      <ExplainIntelligenceButton />
      <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
        Transfer team
      </Button>
      <Button icon={<LineChartOutlined />} onClick={() => navigate(`/growth/orders/${order.id}/status-check`)}>
        Order health check
      </Button>
      {order.order_status === 'Draft' && (
        <>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleStatusChange('Confirmed')}
            loading={updateStatusMutation.isPending}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Confirm Order
          </Button>
          <Button icon={<EditOutlined />} onClick={handleEdit}>
            Edit
          </Button>
        </>
      )}
      {order.order_status === 'Confirmed' && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => handleStatusChange('In Production')}
          loading={updateStatusMutation.isPending}
        >
          Start Production
        </Button>
      )}
      {order.order_status === 'In Production' && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => handleStatusChange('Ready for Delivery')}
          loading={updateStatusMutation.isPending}
        >
          Mark Ready for Delivery
        </Button>
      )}
      {order.order_status === 'Ready for Delivery' && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => handleStatusChange('Delivered')}
          loading={updateStatusMutation.isPending}
        >
          Mark as Delivered
        </Button>
      )}
      <Button icon={<PrinterOutlined />} onClick={handlePrint}>
        Print
      </Button>
      <ReportDocumentActions
        reportKey="sales-order"
        params={{ id: order.id }}
        documentId={order.id}
        sendPermissions={['growth.order.edit', 'growth.view']}
        emailLabel="Email customer"
      />
      {order.order_status === 'Draft' && (
        <Button
          loading={soSubmitLoading}
          onClick={async () => {
            setSoSubmitLoading(true);
            try {
              await salesOrderService.submitForApproval(order.id);
              message.success('Submitted for approval');
              queryClient.invalidateQueries({ queryKey: ['sales-orders', order.id] });
              queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
              queryClient.invalidateQueries({
                queryKey: ['approval-summary', ENTITY_TYPE_SALES_ORDER, order.id],
              });
            } catch (e: any) {
              message.error(e?.response?.data?.message || e?.message || 'Submit failed');
            } finally {
              setSoSubmitLoading(false);
            }
          }}
        >
          Submit for approval
        </Button>
      )}
      {salesReviewPending && order.order_status !== 'Cancelled' && order.approval_status !== 'Approved' && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          loading={recordSalesReviewMutation.isPending}
          onClick={() => recordSalesReviewMutation.mutate(order.id)}
        >
          Mark reviewed
        </Button>
      )}
      {order.approval_status !== 'Approved' &&
        order.order_status !== 'Cancelled' &&
        !order.approval_instance_id &&
        !salesReviewPending && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() =>
            missingShipTo
              ? setAddressModal({ open: true, approveAfter: true })
              : approveOrderMutation.mutate(order.id)
          }
          loading={approveOrderMutation.isPending}
        >
          Approve Order
        </Button>
      )}
      {order.approval_status === 'Approved' &&
        (order.order_status === 'Confirmed' ||
          order.order_status === 'In Production' ||
          order.order_status === 'Ready for Delivery') &&
        order.fulfillment_mode !== 'timesheet' &&
        !order.trading_rental_estimation_id?.trim() &&
        !missingCustomerPoFields && (
        <Button
          type="primary"
          icon={<ToolOutlined />}
          onClick={() => navigate(`/growth/orders/${order.id}/create-work-order`)}
        >
          Convert to Work Order
        </Button>
      )}
      {order.approval_status === 'Approved' &&
        order.fulfillment_mode === 'timesheet' &&
        order.order_status !== 'Cancelled' &&
        order.order_status !== 'Completed' &&
        order.order_status !== 'Draft' && (
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => navigate(`/growth/orders/${order.id}/timesheets`)}
          >
            Create / open timesheet
          </Button>
        )}
      {order.approval_status === 'Approved' &&
        order.fulfillment_mode === 'timesheet' &&
        order.order_status !== 'Cancelled' &&
        order.order_status !== 'Completed' &&
        order.order_status !== 'Draft' && (
          <Popconfirm
            title="Close this customer order?"
            description="No further timesheets or invoices can be created after closing."
            okText="Close order"
            okButtonProps={{ danger: true }}
            onConfirm={() => closeTimesheetOrderMutation.mutate(order.id)}
          >
            <Button
              danger
              icon={<StopOutlined />}
              loading={closeTimesheetOrderMutation.isPending}
            >
              Close customer order
            </Button>
          </Popconfirm>
        )}
      {order.fulfillment_mode === 'timesheet' && order.order_status === 'Completed' && (
        <Tag color="purple">Order completed (timesheet closed)</Tag>
      )}
      {order.approval_status === 'Approved' &&
        order.fulfillment_mode !== 'timesheet' &&
        !!order.trading_rental_estimation_id?.trim() &&
        order.order_status !== 'Cancelled' &&
        order.order_status !== 'Draft' &&
        order.order_status !== 'Delivered' && (
          <Button
            type="primary"
            icon={<CarOutlined />}
            onClick={() =>
              navigate('/growth/delivery-notes', {
                state: {
                  openDirectFromSo: true,
                  salesOrderId: order.id,
                  customerId: order.customer_id,
                },
              })
            }
          >
            Create delivery note
          </Button>
        )}
      {order.fulfillment_mode !== 'timesheet' &&
        (order.order_status === 'Delivered' || deliveriesForSo.length > 0) &&
        order.order_status !== 'Cancelled' && (
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() =>
              navigate('/growth/invoices', {
                state: {
                  openDeliveryInvoice: true,
                  customerId: order.customer_id,
                  salesOrderId: order.id,
                },
              })
            }
          >
            Create invoice from deliveries
          </Button>
        )}
      {order.approval_status === 'Approved' &&
        (toNumber(order.advance_payment_percentage) > 0 || toNumber(order.advance_payment_amount) > 0) && (
          <Button icon={<FileTextOutlined />} onClick={() => setShowAdvanceInvoiceModal(true)}>
            Create Proforma Invoice
          </Button>
        )}
      {order.fulfillment_mode !== 'timesheet' &&
        order.order_status === 'Delivered' &&
        deliveriesForSo.length === 0 && (
        <Button
          icon={<CarOutlined />}
          onClick={() => createRetrospectiveMutation.mutateAsync(order.id)}
          loading={createRetrospectiveMutation.isPending}
        >
          Create delivery note
        </Button>
      )}
      {order.order_status !== 'Delivered' && order.order_status !== 'Cancelled' && (
        <Popconfirm
          title="Cancel Order"
          description="Are you sure you want to cancel this order?"
          onConfirm={() => handleStatusChange('Cancelled')}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DeleteOutlined />}>
            Cancel Order
          </Button>
        </Popconfirm>
      )}
    </>
  );

  const headerFacts = (
    <Space wrap split={<Divider type="vertical" />}>
      <Text>
        <strong>Customer:</strong> {order.customer_name}
      </Text>
      <Text>
        <strong>Sales team:</strong> {order.sales_team_name || '—'}
      </Text>
      <Text>
        <strong>Approval:</strong> {order.approval_status || '—'}
      </Text>
      <Text>
        <strong>Sales review:</strong>{' '}
        {order.sales_review_status === 'pending_review'
          ? 'Awaiting (use Mark reviewed)'
          : order.sales_review_status === 'reviewed'
            ? `Done${order.sales_reviewed_at ? ` (${dayjs(order.sales_reviewed_at).format('DD MMM YYYY HH:mm')})` : ''}`
            : order.sales_review_status || '—'}
      </Text>
      <Text>
        <strong>Total:</strong> {formatCurrency(toNumber(order.grand_total), order.currency)}
      </Text>
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>Quick links</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/growth/delivery-notes')}>
          Delivery notes
        </Button>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/growth/invoices')}>
          Tax invoices
        </Button>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/growth/customers')}>
          Customers
        </Button>
      </Space>
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0', marginBottom: 0 }}>
      {id ? <AiPageContextSync module="sales" docType="SalesOrder" docId={id} /> : null}
      <SalesTeamTransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        entityType="SalesOrder"
        entityId={order.id}
        entityLabel={order.sales_order_number}
        currentTeamName={order.sales_team_name}
        onSuccess={() => queryClient.invalidateQueries()}
      />
      <SalesOrderAddressModal
        open={addressModal.open}
        salesOrderId={order.id}
        salesOrderNumber={order.sales_order_number}
        billingAddress={order.customer_address}
        shippingAddress={order.delivery_address}
        okText={addressModal.approveAfter ? 'Save & Approve' : 'Save'}
        onClose={() => setAddressModal({ open: false, approveAfter: false })}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['sales-orders', order.id] });
          queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
          queryClient.invalidateQueries({
            queryKey: ['approval-summary', ENTITY_TYPE_SALES_ORDER, order.id],
          });
          if (addressModal.approveAfter) approveOrderMutation.mutate(order.id);
        }}
      />
      <TransferHistoryPanel entityType="SalesOrder" entityId={order.id} />
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Customer order"
            documentNumber={order.sales_order_number}
            statusTag={
              <Tag color={getStatusColor(order.order_status)} style={{ fontSize: '13px', padding: '2px 10px' }}>
                {order.order_status}
              </Tag>
            }
            subtitle={
              <Text type="secondary">
                <ShoppingCartOutlined /> {dayjs(order.order_date).format('DD MMM YYYY')}
              </Text>
            }
            facts={headerFacts}
            primaryActions={primaryActions}
          />
        }
        workflow={<WorkflowStrip stages={workflowStages} current={workflowCurrent} compact />}
        rightRail={rightRail}
      >
        {awaitingApproval && (missingShipTo || missingBillTo) && (
          <Alert
            type={missingShipTo ? 'warning' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              missingShipTo
                ? 'Shipping address is required before this order can be approved'
                : 'Billing address is not set on this order'
            }
            action={
              <Button size="small" type="primary" onClick={() => setAddressModal({ open: true, approveAfter: false })}>
                Add billing & shipping address
              </Button>
            }
          />
        )}
        {!!order.approval_instance_id && (
          <div style={{ marginBottom: 16 }}>
            <ApprovalPanel
              entityType={ENTITY_TYPE_SALES_ORDER}
              entityId={order.id}
              approvalActionsBlockedReason={
                salesReviewPending
                  ? 'Sales review must be completed before approvers can act. Use Mark reviewed above.'
                  : missingCustomerPoFields
                    ? 'Customer PO and Customer PO date are required before approval. Edit the customer order to enter them.'
                    : missingShipTo
                      ? 'Shipping address is required before approval. Use "Add billing & shipping address" above.'
                      : undefined
              }
              onApproveViaDocument={async (remarks) => {
                await salesOrderService.approve(order.id, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await salesOrderService.rejectApproval(order.id, payload);
              }}
              extraInvalidateQueryKeys={[['sales-orders', order.id], ['sales-orders']]}
            />
          </div>
        )}
        {order?.id ? (
          <TaskRoutingCard
            sourceDocumentType="SalesOrder"
            sourceDocumentId={order.id}
            nextActionUserId={order.next_action_user_id}
            documentOwnerLabel="Sales owner (order)"
            documentOwnerUserId={order.sales_person}
            documentOwnerEditHint="Edit the sales person on the order edit screen."
            disabled={order.order_status === 'Cancelled' || order.order_status === 'Completed'}
            invalidateQueryKeys={[['sales-orders', order.id]]}
          />
        ) : null}
        <Collapse
          defaultActiveKey={['financial', 'order', 'lines', 'documents']}
          items={[
            {
              key: 'financial',
              label: 'Financial summary & terms',
              children: (
                <>
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Subtotal">
                            <Text strong>
                              {formatCurrency(toNumber(order.subtotal), order.currency)}
                            </Text>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Discount">
                            {formatCurrency(toNumber(order.discount_amount), order.currency)}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="VAT">
                            {formatCurrency(toNumber(order.vat_amount), order.currency)}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small" style={{ background: '#f0f5ff' }}>
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Grand total">
                            <Text strong style={{ color: '#145A54' }}>
                              {formatCurrency(toNumber(order.grand_total), order.currency)}
                            </Text>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small" title="Advance">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Advance %">{toNumber(order.advance_payment_percentage).toFixed(2)}%</Descriptions.Item>
                          <Descriptions.Item label="Advance amount">
                            {formatCurrency(toNumber(order.advance_payment_amount), order.currency)}
                          </Descriptions.Item>
                          {toNumber(order.advance_payment_amount) > 0 && (
                            <Descriptions.Item label="Reversed (on delivery invoices)">
                              <Text type="secondary">
                                {formatCurrency(toNumber(order.advance_reversed_amount), order.currency)}
                              </Text>
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small" title="Retention">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Retention %">{toNumber(order.retention_percentage).toFixed(2)}%</Descriptions.Item>
                          <Descriptions.Item label="Retention amount">
                            {formatCurrency(toNumber(order.retention_amount), order.currency)}
                          </Descriptions.Item>
                          {toNumber(order.retention_amount) > 0 && (
                            <Descriptions.Item label="Withheld (on delivery invoices)">
                              <Text type="secondary">
                                {formatCurrency(toNumber(order.retention_withheld_amount), order.currency)}
                              </Text>
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small" style={{ background: '#f6ffed' }} title="Net receivable">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Amount">
                            <Text strong>
                              {formatCurrency(
                                toNumber(order.grand_total) -
                                  toNumber(order.advance_payment_amount) -
                                  toNumber(order.retention_amount),
                                order.currency
                              )}
                            </Text>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'order',
              label: 'Order information',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Customer">{order.customer_name}</Descriptions.Item>
                  <Descriptions.Item label="Department">
                    {order.department_id
                      ? (() => {
                          const d = departments.find((x) => x.id === order.department_id);
                          return d ? `${d.department_code} - ${d.department_name}` : order.department_id;
                        })()
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Contact">{order.customer_contact || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Email">{order.customer_email || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{order.customer_phone || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Project">{order.project_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Revenue service type">{revenueServiceTypeLabel}</Descriptions.Item>
                  <Descriptions.Item label="Order type">{order.order_type || 'Standard'}</Descriptions.Item>
                  {order.opportunity_id ? (
                    <Descriptions.Item label="Opportunity (CRM)" span={2}>
                      <Space wrap>
                        <Text>
                          <Text strong>
                            {linkedOpportunity?.opportunity_number ?? '—'}
                          </Text>
                          {linkedOpportunity?.opportunity_name
                            ? ` — ${linkedOpportunity.opportunity_name}`
                            : null}
                        </Text>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={() => navigate(`/growth/opportunities/${order.opportunity_id}`)}
                        >
                          Open opportunity
                        </Button>
                      </Space>
                    </Descriptions.Item>
                  ) : null}
                  <Descriptions.Item label="Customer PO">
                    {order.customer_po_number || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Customer PO date">
                    {order.customer_po_date
                      ? dayjs(order.customer_po_date).format('DD MMM YYYY')
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Order date">{dayjs(order.order_date).format('DD MMM YYYY')}</Descriptions.Item>
                  <Descriptions.Item label="Required date">
                    {order.required_date ? dayjs(order.required_date).format('DD MMM YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Promised date">
                    {order.promised_date ? dayjs(order.promised_date).format('DD MMM YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Delivery method">{order.shipping_method || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Payment terms" span={2}>
                    {order.payment_terms || '-'}
                  </Descriptions.Item>
                  {order.customer_address && (
                    <Descriptions.Item label="Billing address" span={2}>
                      {order.customer_address}
                    </Descriptions.Item>
                  )}
                  {order.delivery_address && (
                    <Descriptions.Item label="Delivery address" span={2}>
                      {order.delivery_address}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
            {
              key: 'lines',
              label: `Line items (${order.items?.length ?? 0})`,
              children: (
                <Table
                  columns={columns}
                  dataSource={order.items?.sort((a, b) => a.line_number - b.line_number) || []}
                  rowKey={(r, idx) => r.id ?? `line-${idx}`}
                  pagination={false}
                  scroll={{ x: 1000 }}
                  size="small"
                  bordered
                />
              ),
            },
            ...(order.is_milestone_based && order.milestones && order.milestones.length > 0
              ? [
                  {
                    key: 'milestones',
                    label: `Milestones (${order.milestones.length})`,
                    children: (
                      <Table
                        dataSource={order.milestones.sort(
                          (a: any, b: any) => (a.milestone_number || 0) - (b.milestone_number || 0)
                        )}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        bordered
                        columns={[
                          { title: '#', dataIndex: 'milestone_number', width: 50 },
                          { title: 'Name', dataIndex: 'milestone_name' },
                          { title: 'Description', dataIndex: 'description', ellipsis: true },
                          {
                            title: 'Value',
                            dataIndex: 'milestone_value',
                            render: (val: number) =>
                              formatCurrency(Number(val) || 0, order.currency),
                          },
                          {
                            title: 'Billing %',
                            dataIndex: 'billing_percentage',
                            render: (v: number) => (Number(v) || 0) + '%',
                          },
                          { title: 'Status', dataIndex: 'milestone_status' },
                          {
                            title: 'Completion %',
                            dataIndex: 'completion_percentage',
                            render: (v: number) => (Number(v) || 0) + '%',
                          },
                          { title: 'Invoiced', dataIndex: 'is_invoiced', render: (v: boolean) => (v ? 'Yes' : 'No') },
                        ]}
                      />
                    ),
                  } as const,
                ]
              : []),
            ...((order.customer_notes || order.internal_notes || order.terms_and_conditions)
              ? [
                  {
                    key: 'notes',
                    label: 'Notes & terms',
                    children: (
                      <>
                        {order.customer_notes && (
                          <>
                            <Title level={5}>Customer notes</Title>
                            <Text>{order.customer_notes}</Text>
                            <Divider />
                          </>
                        )}
                        {order.internal_notes && (
                          <>
                            <Title level={5}>Internal notes</Title>
                            <Text>{order.internal_notes}</Text>
                            <Divider />
                          </>
                        )}
                        {order.terms_and_conditions && (
                          <>
                            <Title level={5}>Terms & conditions</Title>
                            <Text>{order.terms_and_conditions}</Text>
                          </>
                        )}
                      </>
                    ),
                  } as const,
                ]
              : []),
            {
              key: 'documents',
              label: 'Documents',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <DocumentUpload entityType="SalesOrder" entityId={id!} onSuccess={() => {}} />
                  <DocumentList entityType="SalesOrder" entityId={id!} />
                  <InheritedAttachmentsCard trailDomain="sales" entityType="SalesOrder" entityId={id} style={{ marginTop: 0 }} />
                </Space>
              ),
            },
          ]}
        />
      </WorkspaceLayout>

      {(order.opportunity_id?.trim() || order.quotation_id?.trim() || order.contract_review_id?.trim() || order.estimation_id?.trim() || order.boq_id?.trim() || order.trading_rental_estimation_id?.trim()) && (
        <Card size="small" title="Commercial lineage" style={{ margin: '16px 24px 0' }}>
          <Space wrap>
            {order.opportunity_id?.trim() ? (
              <Button type="link" style={{ paddingLeft: 0 }} onClick={() => navigate(`/growth/opportunities/${order.opportunity_id}`)}>
                Opportunity
              </Button>
            ) : (
              <Text type="secondary">No opportunity linked</Text>
            )}
            {order.boq_id?.trim() ? (
              <Button type="link" onClick={() => navigate(`/growth/estimations/boq/edit/${order.boq_id}`)}>
                BOQ Estimation
              </Button>
            ) : null}
            {order.estimation_id?.trim() && !order.boq_id?.trim() ? (
              <Button type="link" onClick={() => navigate(`/growth/estimations/multi-item/${order.estimation_id}`)}>
                Estimation
              </Button>
            ) : null}
            {order.trading_rental_estimation_id?.trim() ? (
              <Button type="link" onClick={() => navigate(`/growth/estimations/trading-rental/${order.trading_rental_estimation_id}`)}>
                Trading &amp; Rental Estimation
              </Button>
            ) : null}
            {order.quotation_id?.trim() ? (
              <Button type="link" onClick={() => navigate(`/growth/quotations/${order.quotation_id}`)}>
                Source quotation
              </Button>
            ) : null}
            {order.contract_review_id?.trim() ? (
              <Button type="link" onClick={() => navigate(`/growth/contract-reviews/${order.contract_review_id}`)}>
                Contract Review
              </Button>
            ) : null}
          </Space>
          {linkedOpportunity ? (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Opportunity: </Text>
              <Text>{linkedOpportunity.opportunity_name || linkedOpportunity.id}</Text>
            </div>
          ) : null}
        </Card>
      )}

      {Array.isArray(consolidationLinks) && consolidationLinks.length > 0 ? (
        <Card size="small" title="Source quotations (consolidation)" style={{ margin: '16px 24px 0' }}>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={consolidationLinks as Array<Record<string, unknown>>}
            columns={[
              {
                title: 'Quotation',
                dataIndex: 'quotation_number',
                render: (v, r) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/growth/quotations/${String(r.quotation_id)}`)}
                  >
                    {String(v)}
                  </Button>
                ),
              },
              { title: 'Line', dataIndex: 'line_number', width: 60 },
              { title: 'Item', dataIndex: 'item_code', width: 100 },
              { title: 'Description', dataIndex: 'item_description', ellipsis: true },
              { title: 'Converted qty', dataIndex: 'converted_qty', align: 'right', width: 110 },
            ]}
          />
        </Card>
      ) : null}

      {id ? <RelatedMocsPanel salesOrderId={id} /> : null}

      {/* Conversion Dialog */}
      <CreateAdvanceInvoiceModal
        open={showAdvanceInvoiceModal}
        onClose={() => setShowAdvanceInvoiceModal(false)}
        onSuccess={(invoiceId) => {
          setShowAdvanceInvoiceModal(false);
          navigate(`/growth/invoices/${invoiceId}`);
        }}
        salesOrderId={order.id}
      />
    </div>
  );
};

export default SalesOrderDetailPage;
